import React, { useCallback, useEffect, useRef, useState } from 'react';
import useDetectOutsideClick from 'hooks/useDetectOutsideClick';
import FormInputWrapper from 'components/FormInputWrapper';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { useStore, useUpdateStore } from 'hooks/useStore';
const Dropdown = (props) => {
    const { type, label, placeholder, onChange, options, defaultValue, loading } = props;
    const dropdownRef = useRef(null);
    const [isOpen, setIsOpen] = useDetectOutsideClick(false, dropdownRef);
    const [searchValue, setSearchValue] = useState('');
    const [selectedOption, setSelectedOption] = useState();
    const isInitialLoad = useRef(true);
    const searchRef = useRef(null);
    const previousOptions = useRef([]);
    const country = useStore((s) => s.country);
    const updateStore = useUpdateStore();
    useEffect(() => {
        // Clears station selection whenever location changes
        if (!loading && options && type === 'LOCATION') {
            if (previousOptions.current.length && options.length !== previousOptions.current.length) {
                const emptyOption = { key: '', value: '', text: '' };
                setSelectedOption(emptyOption);
            }
            previousOptions.current = options;
        }
    }, [loading, options, type]);
    useEffect(() => {
        // Sets default selection
        if (!loading && defaultValue && options && !selectedOption) {
            const preSelectedOption = options.find((option) => option.key === defaultValue.toString());
            if (preSelectedOption) {
                setSelectedOption(preSelectedOption);
            }
        }
    }, [defaultValue, loading, options, selectedOption]);
    useEffect(() => {
        // Handles dropdown selections
        if (selectedOption) {
            setIsOpen(false);
            onChange(selectedOption);
            if (type === 'COUNTRY') {
                updateStore((s) => {
                    s.country = selectedOption.value;
                });
            }
        }
    }, [onChange, selectedOption, setIsOpen, type, updateStore]);
    useEffect(() => {
        var _a;
        // Handles switch between input / static text view in dropdown
        if (isOpen && type === 'LOCATION') {
            (_a = searchRef === null || searchRef === void 0 ? void 0 : searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            setSearchValue('');
        }
    }, [isOpen, type]);
    useEffect(() => {
        // Handles auto country selection on load
        if (!loading && country && !defaultValue && type === 'COUNTRY') {
            const selectedCountry = options.find((option) => option.value === country);
            if (selectedCountry && options && selectedCountry) {
                isInitialLoad.current = false;
                setSearchValue(selectedCountry.text);
                setSelectedOption(selectedCountry);
                onChange(selectedCountry);
            }
        }
    }, [country, defaultValue, isInitialLoad, loading, onChange, options, type]);
    const getOptions = useCallback(() => {
        if (type === 'LOCATION') {
            return options.filter((option) => option.text.toLowerCase().includes(searchValue.toLowerCase()));
        }
        else {
            return options;
        }
    }, [options, type, searchValue]);
    return (React.createElement("div", { className: "dropdown", ref: dropdownRef, "data-testid": "dropdown" },
        React.createElement(FormInputWrapper, { label: label, icon: "arrow-down", onIconClick: () => (!loading ? setIsOpen(!isOpen) : undefined), inputWrapperClassName: isOpen ? 'dropdown-open' : '', loading: loading },
            type === 'COUNTRY' && (React.createElement("div", { className: "option" }, (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text) && (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.flag) && (React.createElement(React.Fragment, null,
                React.createElement(Icon.Flag, { countryCode: selectedOption.flag }),
                React.createElement(Text.Base, null,
                    selectedOption.text,
                    " "))))),
            type === 'LOCATION' && (React.createElement(React.Fragment, null, !isOpen ? (React.createElement("div", { className: "selection-text" }, (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text.length) ? (React.createElement("div", { className: "value" }, selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text)) : (React.createElement("div", { className: "placeholder" }, placeholder)))) : (React.createElement("input", { autoFocus: true, ref: searchRef, placeholder: placeholder, value: searchValue, disabled: !options, onChange: (event) => setSearchValue(event.target.value) }))))),
        isOpen && (React.createElement("div", { className: "dropdown-options", "data-testid": "dropdown-options" }, getOptions().map((option) => {
            return (React.createElement("div", { className: "option selectable", "data-testid": "dropdown-option", key: option.key, onClick: () => setSelectedOption(option) },
                option.flag && React.createElement(Icon.Flag, { countryCode: option.flag }),
                React.createElement(Text.Base, null, option.text)));
        })))));
};
export default Dropdown;
